import { FormControlLabel, RadioGroup, Typography } from '@mui/material';
import { Controller, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PhoneHangUpIcon } from 'assets/svg';
import { useAppDispatch } from 'features/app/hooks';
import { NonBillableCommentType } from 'features/call/call-comments/enums';
import {
  VrsCallCommentsBasePopup,
  VrsCallCommentsFormButtons,
  VrsCallCommentsTextAreaControl,
} from 'features/call/vrs-call-comments/components';
import { useVrsCallCommentsFormSubmit } from 'features/call/vrs-call-comments/hooks';
import type { VrsCallCommentsNonBillableForm } from 'features/call/vrs-call-comments/interfaces';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { Radio } from 'features/ui/elements';
import { setTerminationType } from 'features/call/call-base/store';
import { TerminationType } from 'features/call/call-base/enums';

export const VrsCallCommentsNonBillable = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation', {
    keyPrefix: 'callCommentsNonBillable',
  });
  const { t: reasonTranslate } = useTranslation('translation', {
    keyPrefix: 'callCommentsNonBillable.reason',
  });
  const { handleSubmit, control, watch } =
    useForm<VrsCallCommentsNonBillableForm>({
      mode: 'onChange',
      defaultValues: {
        nonBillableType: null,
      },
    });
  const { isValid } = useFormState({ control });
  const watchNonBillableType = watch('nonBillableType');
  const { submitCallComments } = useVrsCallCommentsFormSubmit();
  const submitForm = async ({
    nonBillableType,
    commentText,
  }: VrsCallCommentsNonBillableForm) => {
    if (!nonBillableType) {
      return;
    }

    dispatch(setTerminationType(TerminationType.NonBill));

    await submitCallComments({
      commentTypes: [nonBillableType],
      commentText,
    });
  };

  return (
    <VrsCallCommentsBasePopup
      icon={<StyledHangUpIcon title='non-billable-icon' />}
      title={t('title')}
      subtitle={t('subtitle')}
    >
      <StyledForm onSubmit={handleSubmit(submitForm)}>
        <ScrollableContainer>
          <Controller
            control={control}
            name='nonBillableType'
            rules={{ required: true }}
            render={({ field: { onChange, ...args } }) => (
              <StyledRadioGroup {...args} onChange={onChange}>
                <RadioControlLabel
                  value={NonBillableCommentType.VRI}
                  label={reasonTranslate('VRI')}
                  control={<Radio />}
                />
                <RadioControlLabel
                  value={NonBillableCommentType._911VRI}
                  label={reasonTranslate('911VRI')}
                  control={<Radio />}
                />
                <RadioControlLabel
                  value={NonBillableCommentType.TESTTRN}
                  label={reasonTranslate('testtrn')}
                  title='testtrn'
                  control={<Radio />}
                />
                <RadioControlLabel
                  value={NonBillableCommentType.IllegVCO}
                  label={reasonTranslate('IllegVCO')}
                  control={<Radio />}
                />
                <RadioControlLabel
                  value={NonBillableCommentType.OTHERNBL}
                  label={reasonTranslate('other')}
                  title='other'
                  control={<Radio />}
                />
              </StyledRadioGroup>
            )}
          />
          {watchNonBillableType === NonBillableCommentType.OTHERNBL && (
            <>
              <Typography variant='label' color={theme.palette.grey[300]}>
                {t('description')}
              </Typography>
              <VrsCallCommentsTextAreaControl control={control} />
            </>
          )}
        </ScrollableContainer>
        <VrsCallCommentsFormButtons isValid={isValid} />
      </StyledForm>
    </VrsCallCommentsBasePopup>
  );
};

const StyledForm = styled('form')`
  width: 100%;
  margin-top: ${theme.spacing(16)};
`;

const ScrollableContainer = styled('div')`
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledRadioGroup = styled(RadioGroup)`
  width: 100%;
`;

const RadioControlLabel = styled(FormControlLabel)`
  margin-top: ${theme.spacing(4)};
`;

const StyledHangUpIcon = styled(PhoneHangUpIcon)`
  width: 62px;
  height: 62px;
`;
